/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { ImageLoaderProps } from './ImageLoaderProps';
import { ImageLoaderTypes } from './ImageLoaderTypes';

import './ImageLoader.scss';

const ImageLoader: FunctionComponent<ImageLoaderProps> = ({
    src,
    placeholder = '',
    imageType,
    dataTestId,
    handleOnImageLoaded,
}): JSX.Element => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        if (src) {
            imageRef.current = new Image();
            imageRef.current.src = src;
            imageRef.current.onload = () => {
                setIsImageLoaded(true);
                handleOnImageLoaded?.(true);
            };
            imageRef.current.onerror = () => {
                setIsImageLoaded(false);
                handleOnImageLoaded?.(false);
            };
        } else {
            setIsImageLoaded(false);
            handleOnImageLoaded?.(false);
        }

        return () => {
            if (imageRef.current) {
                imageRef.current.onload = null;
                imageRef.current.onerror = null;
                imageRef.current = null;
            }
        };
    }, [isImageLoaded, src]);

    const imageLoaderClass =
        imageType === ImageLoaderTypes.STANDARD_INFO_BANNER_CHANNEL_BRANDING
            ? 'image-loader-no-animation'
            : 'image-loader';

    // placeholderClass will be assigned according to imageType
    let placeholderClass = ImageLoaderTypes.DEFAULT;
    if (imageType === ImageLoaderTypes.CHANNEL_BRANDING_IMAGE) {
        placeholderClass = `channel-branding-text ${
            placeholder && placeholder.length > 8 ? 'adjust-font-size' : ''
        }`;
    } else if (imageType === ImageLoaderTypes.STANDARD_INFO_BANNER_CHANNEL_BRANDING) {
        placeholderClass = `standard-info-banner-channel-branding-text ${
            placeholder && placeholder.length > 8 ? 'adjust-font-size' : ''
        }`;
    }
    return (
        <div className={imageType} data-testid={imageType}>
            {!isImageLoaded && <div className={placeholderClass}>{placeholder}</div>}

            {src && (
                <img
                    src={src}
                    className={isImageLoaded ? imageLoaderClass : 'hidden'}
                    loading="lazy"
                    data-testid={dataTestId}
                />
            )}
        </div>
    );
};

ImageLoader.propTypes = {
    src: PropTypes.string,
    placeholder: PropTypes.string,
    imageType: PropTypes.string,
    dataTestId: PropTypes.string,
};

export default ImageLoader;
